import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges
} from "@angular/core";
import { CryptocoinService } from "@main-services/currency/cryptocoin.service";
import { ContactField, Cryptocoin } from "@models";
import { SendTransaction } from "@models/transaction";
import { BehaviorSubject, Observable } from "rxjs";
import { debounceTime, distinctUntilChanged, map, skip } from "rxjs/operators";
import { INFINITE_SEARCH_DELAY } from "@helpers/constants";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { AssetsContactsScrollService } from "@main-services/assets-contacts-scroll.service";
import { containsString } from "@helpers/utils";
import { BpSelectOption } from "@modules/core/components/bp-select/bp-select.component";

@UntilDestroy()
@Component({
	selector: "contact-address-select",
	templateUrl: "./contact-address-select.component.html",
	styleUrls: ["./contact-address-select.component.scss"],
	providers: [AssetsContactsScrollService],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class ContactAddressSelectComponent implements OnInit, OnChanges {
	@Input("contactField") public contactField: ContactField = null;
	@Input("validationError") public validationError: string = "";
	@Input("selectedIds") public selectedIds: string[] = [];
	@Output("saveEmit") public saveEmit: EventEmitter<void> = new EventEmitter<void>();
	@Output("deleteEmit") public deleteEmit: EventEmitter<void> = new EventEmitter<void>();
	@Output("selectedIdEmitter") public selectedIdEmitter: EventEmitter<string> = new EventEmitter<string>();

	public currentCoin: Cryptocoin = null;
	public getDtTypes: BpSelectOption[] = SendTransaction.Types;
	public searchInput$: BehaviorSubject<string> = new BehaviorSubject<string>("");

	public coinOptions$: Observable<BpSelectOption[]> = this.assetScrollService.getListObservable().pipe(
		map((options: BpSelectOption[]) => {
			// Always adds current selection at the top of the list -> Necessary because selection might not be part of the initial fetch
			const currentOption: BpSelectOption = this.assetScrollService.parseCoinToOption(this.currentCoin);
			return [currentOption, ...options];
		})
	);

	public constructor(
		private cryptocoinService: CryptocoinService,
		private assetScrollService: AssetsContactsScrollService
	) {
		this.searchInput$
			.pipe(
				skip(1), // Needed to skip empty value on searchSub init!
				debounceTime(INFINITE_SEARCH_DELAY),
				distinctUntilChanged(),
				untilDestroyed(this)
			)
			.subscribe((value) => this.assetScrollService.searchListUpdate(value));
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes.hasOwnProperty("selectedIds")) {
			const previousValue: string[] = changes["selectedIds"]["previousValue"] || [];
			const currentValue: string[] = changes["selectedIds"]["currentValue"] || [];
			let previousCoinId: string = "";
			previousValue.forEach((id) => {
				if (!currentValue.includes(id)) {
					previousCoinId = id;
				}
			});
			this.assetScrollService.updateSelectedIds(this.selectedIds);
			// Re-adds previous selection into the list if it was filtered out before
			this.assetScrollService.reFilterList(previousCoinId);
		}
	}

	public ngOnInit(): void {
		this.currentCoin = this.cryptocoinService.getItemById(
			this.getCoinFromFieldType(this.contactField.getType()).getId()
		);
		this.assetScrollService.updateSelectedIds(this.selectedIds);
		this.assetScrollService.initList();
	}

	public getCurrentCoinName(): string {
		if (this.currentCoin) {
			return this.currentCoin.getName();
		}
		return "";
	}

	public getCustomSearch(): Function {
		return (searchInput: string, item: BpSelectOption) => {
			const field: string = item.value;
			const coin: Cryptocoin = this.getCoinFromFieldType(field);

			if (!coin) {
				return false;
			}
			if (containsString(coin.getSymbol(), searchInput)) {
				return true;
			}
			// By tradable name
			return containsString(coin.getName(), searchInput);
		};
	}

	public fetchListUpdate(): void {
		// FetchesList on page-end while scrolling
		this.assetScrollService.fetchListUpdate();
	}

	public searchListUpdate(searchQuery: string = ""): void {
		// Deals with search-queries in the open dropdown
		this.searchInput$.next(searchQuery);
	}

	public getLoadingState$(): Observable<boolean> {
		// Activates loading state of bp-select
		return this.assetScrollService.getLoadingObservable();
	}

	private getCoinFromFieldType(type: string): Cryptocoin {
		const coinSymbol: string = type.split("_")[0];
		return this.cryptocoinService.findBySymbol(coinSymbol);
	}

	public getPlaceholderName(): string {
		if (this.isStellar()) {
			return "stellar_memo";
		}
		return this.currentCoin.getSymbol(true) + "_destination_tag";
	}

	public isStellar(): boolean {
		return this.currentCoin.isStellar();
	}

	public onContactDropdownChange(type: string): void {
		const coin: Cryptocoin = this.getCoinFromFieldType(type);
		this.currentCoin = coin;
		// Updates list of selected coinIDs
		this.selectedIdEmitter.emit(coin.getId());
	}
}
